import React, { useEffect, useMemo, useState } from 'react';
import { Input, Card, Row, Col, Empty, Space, Tag, Typography } from 'antd';
import { SearchOutlined, CheckCircleOutlined, DatabaseOutlined, EditOutlined } from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { getEmbeddingModels, registerEmbeddingModel } from '../../services/api/models';
import { resolveModelIconUrl } from '../../utils/modelIconUrl';
import './ModelFields.css';

const { Text, Paragraph } = Typography;

const DEBOUNCE_MS = 200;

/**
 * Search embedding models by name/family; debounced filter smooths fast typing.
 * When nothing matches, offers registering the typed name in the catalog.
 */
const EmbeddingModelField = ({ value, onChange, models: modelsProp = [], onModelsUpdated }) => {
    const { keycloak } = useKeycloak();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [registering, setRegistering] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    const debouncedTrim = debouncedSearch.trim();
    const debouncedLower = debouncedTrim.toLowerCase();

    const sortedBase = useMemo(
        () =>
            [...modelsProp].sort((a, b) => {
                const ai = a.installed ? 1 : 0;
                const bi = b.installed ? 1 : 0;
                if (bi !== ai) return bi - ai;
                return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
            }),
        [modelsProp],
    );

    const filtered = useMemo(() => {
        if (!debouncedTrim) return sortedBase;
        return sortedBase.filter((m) => {
            const name = (m.name || '').toLowerCase();
            const fam = (m.family_name || '').toLowerCase();
            const desc = (m.family_description || '').toLowerCase();
            return name.includes(debouncedLower) || fam.includes(debouncedLower) || desc.includes(debouncedLower);
        });
    }, [sortedBase, debouncedTrim, debouncedLower]);

    const exactInCatalog = debouncedTrim
        ? modelsProp.some((m) => (m.name || '').toLowerCase() === debouncedLower)
        : false;

    const showUseCustom = Boolean(
        debouncedTrim.length > 0 &&
            !exactInCatalog &&
            filtered.length === 0 &&
            keycloak?.authenticated,
    );

    const handleRegisterCustom = async () => {
        if (!debouncedTrim || registering) return;
        setRegistering(true);
        try {
            const created = await registerEmbeddingModel(keycloak, debouncedTrim);
            const list = await getEmbeddingModels(keycloak);
            if (Array.isArray(list)) {
                onModelsUpdated?.(list);
            }
            if (created && created.id != null) {
                onChange?.(created.id);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setRegistering(false);
        }
    };

    return (
        <>
            <Input
                placeholder="Search embedding models..."
                prefix={<SearchOutlined style={{ color: 'var(--ant-color-text-quaternary, #bbb)' }} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                className="create-collection-search-input"
            />
            <div className="create-collection-embedding-picker-wrap">
                <div className="create-collection-model-scroll">
                    {filtered.length > 0 ? (
                        <Row gutter={[8, 8]}>
                            {filtered.map((m) => {
                                const isSelected = value != null && Number(value) === Number(m.id);
                                return (
                                    <Col span={24} key={m.id}>
                                        <Card
                                            size="small"
                                            hoverable
                                            onClick={() => onChange?.(m.id)}
                                            className={`create-collection-model-card ${isSelected ? 'is-selected' : ''}`}
                                            bodyStyle={{ padding: '10px 12px' }}
                                        >
                                            <div className="create-collection-embedding-card-inner">
                                                <div className="create-collection-embedding-card-main">
                                                    {m.family_icon ? (
                                                        <img
                                                            src={resolveModelIconUrl(m.family_icon)}
                                                            alt=""
                                                            className="create-collection-embedding-card-icon"
                                                        />
                                                    ) : (
                                                        <div className="create-collection-embedding-card-icon-fallback">
                                                            <DatabaseOutlined />
                                                        </div>
                                                    )}
                                                    <div className="create-collection-embedding-card-text">
                                                        <div className="create-collection-embedding-title-row">
                                                            <Text strong className="create-collection-embedding-card-name">
                                                                {m.name}
                                                            </Text>
                                                            {m.installed && <Tag color="success">Installed</Tag>}
                                                        </div>
                                                        {m.family_description && (
                                                            <Paragraph
                                                                type="secondary"
                                                                ellipsis={{ rows: 2 }}
                                                                className="create-collection-embedding-card-desc"
                                                            >
                                                                {m.family_description}
                                                            </Paragraph>
                                                        )}
                                                        <Space size={4} wrap className="create-collection-embedding-tags">
                                                            {m.size && <Tag>{m.size}</Tag>}
                                                            {m.context && <Tag>{m.context}</Tag>}
                                                            {m.input && <Tag>{m.input}</Tag>}
                                                        </Space>
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : showUseCustom ? (
                        <Row gutter={[8, 8]}>
                            <Col span={24}>
                                <Card
                                    size="small"
                                    hoverable
                                    role="button"
                                    tabIndex={0}
                                    loading={registering}
                                    onClick={handleRegisterCustom}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleRegisterCustom();
                                        }
                                    }}
                                    className="create-collection-model-card local-ollama-use-custom-card"
                                    bodyStyle={{ padding: '10px 12px' }}
                                >
                                    <div className="create-collection-embedding-card-inner">
                                        <div className="create-collection-embedding-card-main">
                                            <div className="create-collection-embedding-card-icon-fallback local-ollama-use-custom-icon">
                                                <EditOutlined />
                                            </div>
                                            <div className="create-collection-embedding-card-text">
                                                <Text strong className="create-collection-embedding-card-name">
                                                    Use this model name
                                                </Text>
                                                <Paragraph type="secondary" className="local-ollama-use-custom-code" copyable={{ text: debouncedTrim }}>
                                                    {debouncedTrim}
                                                </Paragraph>
                                                <Text type="secondary" className="local-ollama-use-custom-hint">
                                                    Not in the list — click to add it to the catalog and select it.
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                modelsProp.length ? 'No models match your search.' : 'No embedding models available.'
                            }
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default EmbeddingModelField;
